import { useEffect, useState } from "react";
import api from "../../lib/api";
import {
  Stethoscope, Pill, FileBadge, Send, Activity, StickyNote, RefreshCw, Loader2,
} from "lucide-react";

const ACTIONS = [
  { id: "consulta", label: "Iniciar consulta", icon: Stethoscope },
  { id: "receta", label: "Crear receta", icon: Pill },
  { id: "certificado", label: "Certificado", icon: FileBadge },
  { id: "referido", label: "Referir", icon: Send },
  { id: "vitales", label: "Signos vitales", icon: Activity },
  { id: "nota", label: "Nueva nota", icon: StickyNote },
];

const DOT = { high: "bg-[#FF6B6B]", medium: "bg-[#FFD166]", info: "bg-white/80" };

export const PatientBrief = ({ patientId }) => {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = (refresh = false) => {
    setLoading(true);
    api
      .get(`/ai/patient-brief/${patientId}`, { params: refresh ? { refresh: true } : {} })
      .then(({ data }) => setBrief(data))
      .catch(() => setBrief(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setBrief(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const act = (action) =>
    window.dispatchEvent(new CustomEvent("consu:patient-action", { detail: { action, patientId } }));

  const p = brief?.patient;
  return (
    <div className="space-y-2.5 cai-fade-in" data-testid="cai-patient-brief">
      <div className="cai-glass rounded-[22px] p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/70">Paciente actual</div>
            <div className="text-[15px] font-semibold text-white truncate" data-testid="cai-patient-name">
              {p?.full_name || "Cargando…"}
            </div>
            {p && (
              <div className="text-[11.5px] text-white/75">
                {[p.age != null && `${p.age} años`, p.blood_type && `Sangre ${p.blood_type}`, `${brief.stats.consultas} consultas`]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => load(true)}
            disabled={loading}
            title="Regenerar resumen"
            data-testid="cai-brief-refresh"
            className="w-8 h-8 shrink-0 rounded-full cai-glass cai-glass-hover grid place-items-center text-white"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </button>
        </div>
        <p className="mt-2.5 text-[12.5px] leading-relaxed text-white/95" data-testid="cai-brief-summary">
          {loading && !brief ? "Analizando el expediente…" : brief?.summary}
        </p>
        {brief?.alerts?.length > 0 && (
          <div className="mt-3 space-y-1.5" data-testid="cai-brief-alerts">
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/70">Advertencias</div>
            {brief.alerts.map((a, i) => (
              <div key={i} className="flex items-start gap-2 text-[12px] text-white" data-testid={`cai-alert-${i}`}>
                <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${DOT[a.level] || DOT.info}`} />
                <span className="leading-snug">{a.text}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2">
        {ACTIONS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => act(id)}
            data-testid={`cai-action-${id}`}
            className="cai-glass cai-glass-hover rounded-[18px] p-3 text-left text-white"
          >
            <Icon className="w-[18px] h-[18px]" strokeWidth={1.6} />
            <div className="mt-2 text-[12px] leading-tight">{label}</div>
          </button>
        ))}
      </div>
    </div>
  );
};
